import {
  AfterSaveActionEnum,
  DataEntityPresenterModel,
  DataViewAvailableDisplayModel,
  DataViewBadgeFaceConfiguration,
  DataViewCalendarFaceConfiguration,
  DataViewKanbanFaceConfiguration,
  DataViewMiniCardFaceConfiguration,
  DataViewsPresenterModel,
  DataViewTableFaceConfiguration,
  DataViewTimelineFaceConfiguration,
  DataViewTreeFaceConfiguration,
  EntityViewTypeEnum,
  EntryActionOperationEnum,
  FieldPresenterModel,
  FieldViewOptionValueTypeEnum,
  LocalizedValueModel,
  REQUEST_ENTITY_ID,
  TEMP_FIELD_COMPONENT_NAMES,
} from '@osos/shared/types';
import cloneDeep from 'lodash-es/cloneDeep';
import {
  findFieldBasedOnShowOption,
  findFieldByComponentName,
  hasEditPermission,
  isVirtualJoinerEntity,
  isVirtualProxyEntity,
} from '../../utils';
import {
  BadgeViewUIConfiguration,
  CalendarViewUIConfiguration,
  DataViewUIConfiguration,
  EditEntryConfiguration,
  EntriesViewTypes,
  GeneralUIViewConfiguration,
  KanbanViewUIConfiguration,
  MiniCardViewUIConfiguration,
  TableViewUIConfiguration,
  TimelineViewUIConfiguration,
  TreeViewUIConfiguration,
  ViewEntryConfiguration,
} from './data-view-ui-configuration';

export class DataViewUIConfigurationModel
  extends GeneralUIViewConfiguration
  implements DataViewUIConfiguration
{
  constructor(
    private config: DataViewsPresenterModel,
    dataEntity: DataEntityPresenterModel,
  ) {
    super(config, dataEntity);
    this.init();
  }

  init(): void {
    this.entriesViewTypes = {} as EntriesViewTypes;
    this.initViewConfig();
  }

  initViewConfig(): void {
    if (this.config.viewConfiguration?.enabled) {
      this.config.viewConfiguration.availableDisplays?.forEach((_view) => {
        switch (_view.viewType) {
          case EntityViewTypeEnum.TableView:
            this.initTableViewPermissions(
              _view.configuration.tableViewConfiguration,
            );
            break;
          case EntityViewTypeEnum.KanbanBoard:
            this.initKanbanViewPermissions(
              _view.configuration.kanbanViewConfiguration,
            );
            break;
          case EntityViewTypeEnum.CalenderView:
            this.initCalendarViewPermissions(
              _view.configuration.calendarViewConfiguration,
            );
            break;
          case EntityViewTypeEnum.CardView:
            this.initBadgeViewPermissions(
              _view.configuration.badgeViewConfiguration,
            );
            break;
          case EntityViewTypeEnum.Timeline:
            this.initTimelineViewPermissions(
              _view.configuration.timelineViewConfiguration,
            );
            break;
          case EntityViewTypeEnum.TreeView:
            this.initTreeViewPermissions(
              _view.configuration.treeViewConfiguration,
            );
            break;
          case EntityViewTypeEnum.MiniCard:
            this.initMiniCardViewPermissions(
              _view.configuration.miniCardViewConfiguration,
            );
            break;
          default:
            break;
        }
      });
    }
  }
  initTableViewPermissions(tableConfig: DataViewTableFaceConfiguration): void {
    this.entriesViewTypes.tableView = new TableViewUIConfigurationModel(
      this.dataEntity,
      this.config,
      tableConfig,
    );
  }

  initKanbanViewPermissions(
    kanbanConfig: DataViewKanbanFaceConfiguration,
  ): void {
    this.entriesViewTypes.kanbanView = new KanbanViewUIConfigurationModel(
      this.dataEntity,
      this.config,
      kanbanConfig,
    );
  }

  initTreeViewPermissions(treeViewConfig: DataViewTreeFaceConfiguration): void {
    this.entriesViewTypes.treeView = new TreeViewUIConfigurationModel(
      this.dataEntity,
      this.config,
      treeViewConfig,
    );
  }

  initCalendarViewPermissions(
    calendarViewConfiguration: DataViewCalendarFaceConfiguration,
  ): void {
    this.entriesViewTypes.calendarView = new CalendarViewUIConfigurationModel(
      this.dataEntity,
      this.config,
      calendarViewConfiguration,
    );
  }

  initBadgeViewPermissions(
    badgeViewConfiguration: DataViewBadgeFaceConfiguration,
  ): void {
    this.entriesViewTypes.badgeView = new BadgeViewUIConfigurationModel(
      this.dataEntity,
      this.config,
      badgeViewConfiguration,
    );
  }

  initTimelineViewPermissions(config: DataViewTimelineFaceConfiguration): void {
    this.entriesViewTypes.timelineView = new TimelineViewUIConfigurationModel(
      this.dataEntity,
      config,
    );
  }
  initMiniCardViewPermissions(
    miniCardConfig: DataViewMiniCardFaceConfiguration,
  ): void {
    this.entriesViewTypes.miniCard = new MiniCardViewUIConfigurationModel(
      this.dataEntity,
      this.dataView,
      miniCardConfig,
    );
  }
  getViewType(viewType: EntityViewTypeEnum): DataViewAvailableDisplayModel {
    const _view = this.config.viewConfiguration.availableDisplays.find(
      (_avView) => _avView.viewType === viewType,
    );
    return _view;
  }

  getDefaultView(): EntityViewTypeEnum {
    return (
      this.config.viewConfiguration.availableDisplays.find(
        (_view) => _view.isDefault,
      )?.viewType ?? EntityViewTypeEnum.TableView
    );
  }

  hasViewPermission(): boolean {
    return this.config.viewConfiguration.enabled;
  }
  hasMultipleView(): boolean {
    return this.config.viewConfiguration.availableDisplays.length > 1;
  }

  hasShowMessageAction(operation: EntryActionOperationEnum): boolean {
    return (
      this._getSupportedOperationConfiguration(operation)?.afterSave?.action ===
      AfterSaveActionEnum.ShowMessage
    );
  }
  hasReturnToEntryView(operation: EntryActionOperationEnum): boolean {
    return (
      this._getSupportedOperationConfiguration(operation)?.afterSave?.action ===
        AfterSaveActionEnum.RedirectToEntryView &&
      this.dataView.entriesConfiguration?.enabled
    );
  }
  getMessage(operation: EntryActionOperationEnum): LocalizedValueModel {
    if (this.hasShowMessageAction(operation)) {
      return this._getSupportedOperationConfiguration(operation).afterSave
        ?.message;
    }
    return { en: 'success', ar: 'نجاح العملية' };
  }

  getTableViewFields(): string[] {
    const _viewConfig = this.dataView.viewConfiguration.availableDisplays?.find(
      (_view) => _view.viewType === EntityViewTypeEnum.TableView,
    );
    if (_viewConfig) {
      return _viewConfig.configuration.tableViewConfiguration.fields;
    }
    return [];
  }
}

export class TableViewUIConfigurationModel extends TableViewUIConfiguration {
  constructor(
    dataEntity: DataEntityPresenterModel,
    config?: DataViewsPresenterModel,
    private tableConfig?: DataViewTableFaceConfiguration,
  ) {
    super(dataEntity, config);
    this.display = true;
    this.selectRecords =
      this.allowUserCollections ||
      dataEntity.workflows?.length > 0 ||
      dataEntity.id === REQUEST_ENTITY_ID ||
      this.archiveRecords;
    this.showPagination = true;
    this.allowSort = config?.viewConfiguration?.allowChangeSorting;
    this.allowChangeFields = this.tableConfig?.allowChangeFields;
    this.displyedFields = this.initDisplyedFields();
    this.maxDisplayedValues = this.tableConfig.maxDisplayedValues;
    this.isScrollable = true;
  }

  initDisplyedFields(): FieldPresenterModel[] {
    if (this.tableConfig?.fields) {
      return this.filterFields(this.tableConfig.fields);
    }
    return [];
  }
}

export class TableViewUIConfigurationDefaultModel extends TableViewUIConfiguration {
  constructor(dataEntity: DataEntityPresenterModel) {
    super(dataEntity);
    this.display = true;
    this.showPagination = true;
    this.selectRecords = true;
    this.allowChangeFields = false;
    this.displyedFields = this.initDisplyedFields();
    this.maxDisplayedValues = undefined;
    this.isScrollable = true;
    this.allowQuickCreate = true;
    this.allowSort = true;
    this.allowDuplicateEntry = true;
    this.allowCreateInvalidEntry = true;
  }

  initDisplyedFields(): FieldPresenterModel[] {
    return this.dataEntity.fields;
  }
}

export class KanbanViewUIConfigurationModel extends KanbanViewUIConfiguration {
  constructor(
    dataEntity: DataEntityPresenterModel,
    config: DataViewsPresenterModel,
    private kanbanConfig: DataViewKanbanFaceConfiguration,
  ) {
    super(dataEntity, config);
    this.display = true;

    this.selectRecords = this.archiveRecords || this.allowUserCollections;
    const categoryField = this.getCategoryField();
    const editableFields =
      this._getSupportedOperationConfiguration(
        EntryActionOperationEnum.AllowEditEntries,
      )?.fields ?? [];
    this.allowDragAndDrop =
      hasEditPermission(config) &&
      !!categoryField &&
      !!editableFields.includes(categoryField._normalizedName) &&
      !isVirtualJoinerEntity(dataEntity) &&
      !isVirtualProxyEntity(dataEntity);
    this.displyedFields = this.initDisplyedFields();
    this.showMetaData = kanbanConfig?.showMetaData || false;
    this.selectedCategories = kanbanConfig?.categories || [];
  }

  initDisplyedFields(): FieldPresenterModel[] {
    const _viewOptions: {
      [key in keyof typeof FieldViewOptionValueTypeEnum]: string;
    } = {
      showAsTitle: this.kanbanConfig.fieldsMapping.mainTitle,
      showAsSubTitle: this.kanbanConfig.fieldsMapping.subTitle,
      showAsEntryPicture: this.kanbanConfig.fieldsMapping.picture,
      showAsMainDate: this.kanbanConfig.fieldsMapping.mainDateField,
      showAsCategory: this.kanbanConfig.fieldsMapping.categoryField,
      showAsSubCategory: this.kanbanConfig.fieldsMapping.subCategoryField,
      showAsRelationField: this.kanbanConfig.fieldsMapping.relationField,
      showAsMainNumber: this.kanbanConfig.fieldsMapping.mainNumberField,
      showAsMainPhone: null,
    };
    this.displyedFields = this.mapDisplayOptions(
      cloneDeep(this.dataEntity.fields),
      _viewOptions,
    );

    return this.displyedFields;
  }

  getCategoryField(): FieldPresenterModel {
    const _categoryFields = findFieldByComponentName(
      this.dataEntity?.fields,
      TEMP_FIELD_COMPONENT_NAMES.categoryComponent,
      'editComponent',
    );
    return _categoryFields.find(
      (field) =>
        field._normalizedName === this.kanbanConfig.fieldsMapping.categoryField,
    );
  }
}

export class TreeViewUIConfigurationModel extends TreeViewUIConfiguration {
  constructor(
    dataEntity: DataEntityPresenterModel,
    config: DataViewsPresenterModel,
    private treeViewConfig: DataViewTreeFaceConfiguration,
  ) {
    super(dataEntity, config);
    this.display = true;
    this.displyedFields = this.initDisplyedFields();
  }

  getTreeReferenceNormalizedName(): FieldPresenterModel {
    return this.dataEntity?.fields?.find(
      (field) =>
        field.normalizedName ===
        this.treeViewConfig?.fieldsMapping?.treeReference,
    );
  }

  initDisplyedFields(): FieldPresenterModel[] {
    const _viewOptions: {
      [key in keyof typeof FieldViewOptionValueTypeEnum]: string;
    } = {
      showAsTitle: this.treeViewConfig.fieldsMapping.mainTitle,
      showAsSubTitle: this.treeViewConfig.fieldsMapping.subTitle,
      showAsEntryPicture: this.treeViewConfig.fieldsMapping.icon,
      showAsMainDate: null,
      showAsCategory: null,
      showAsSubCategory: null,
      showAsRelationField: this.treeViewConfig.fieldsMapping.reference,
      showAsMainPhone: null,
      showAsMainNumber: null,
    };
    this.displyedFields = this.mapDisplayOptions(
      cloneDeep(this.dataEntity.fields),
      _viewOptions,
    );

    return this.displyedFields;
  }
}
export class TreeViewUIConfigurationDefaultModel extends TreeViewUIConfiguration {
  constructor(dataEntity: DataEntityPresenterModel) {
    super(dataEntity);
    this.display = true;
    this.displyedFields = this.initDisplyedFields();
  }

  initDisplyedFields(): FieldPresenterModel[] {
    return this.dataEntity.fields;
  }

  getTreeReferenceNormalizedName(): FieldPresenterModel {
    return undefined;
  }
}

export class KanbanViewUIConfigurationDefaultModel extends KanbanViewUIConfiguration {
  constructor(dataEntity: DataEntityPresenterModel) {
    super(dataEntity);
    this.display = true;
    this.allowDragAndDrop =
      !isVirtualJoinerEntity(dataEntity) && !isVirtualProxyEntity(dataEntity);
    this.selectRecords = true;
    this.displyedFields = this.initDisplyedFields();
    this.showMetaData = true;
    this.selectedCategories = [];
  }

  initDisplyedFields(): FieldPresenterModel[] {
    return this.dataEntity.fields;
  }

  getCategoryField(): FieldPresenterModel {
    const _categoryField = findFieldBasedOnShowOption(
      this.dataEntity.fields,
      FieldViewOptionValueTypeEnum.showAsCategory,
    );
    if (_categoryField) {
      return _categoryField;
    }
    return findFieldByComponentName(
      this.dataEntity?.fields,
      TEMP_FIELD_COMPONENT_NAMES.categoryComponent,
      'editComponent',
    )?.[0];
  }
}

export class BadgeViewUIConfigurationModel extends BadgeViewUIConfiguration {
  constructor(
    dataEntity: DataEntityPresenterModel,
    config: DataViewsPresenterModel,
    private badgeViewConfig: DataViewBadgeFaceConfiguration,
  ) {
    super(dataEntity, config);
    this.display = true;

    this.selectRecords = this.archiveRecords || this.allowUserCollections;

    this.displyedFields = this.initDisplyedFields();
    this.showMetaData = this.badgeViewConfig.showMetaData;
  }

  initDisplyedFields(): FieldPresenterModel[] {
    const _viewOptions: {
      [key in keyof typeof FieldViewOptionValueTypeEnum]: string;
    } = {
      showAsTitle: this.badgeViewConfig.fieldsMapping.mainTitle,
      showAsSubTitle: this.badgeViewConfig.fieldsMapping.subTitle,
      showAsEntryPicture: this.badgeViewConfig.fieldsMapping.picture,
      showAsMainNumber: this.badgeViewConfig.fieldsMapping.mainNumberField,
      showAsMainDate: null,
      showAsCategory: null,
      showAsSubCategory: null,
      showAsRelationField: null,
      showAsMainPhone: null,
    };
    this.displyedFields = this.mapDisplayOptions(
      cloneDeep(this.dataEntity.fields),
      _viewOptions,
    );

    return this.displyedFields;
  }
}

export class BadgeViewUIConfigurationDefaultModel extends BadgeViewUIConfiguration {
  constructor(dataEntity: DataEntityPresenterModel) {
    super(dataEntity);
    this.display = true;
    this.selectRecords = true;
    this.displyedFields = this.initDisplyedFields();
    this.showMetaData = true;
  }

  initDisplyedFields(): FieldPresenterModel[] {
    return this.dataEntity.fields;
  }
}

export class CalendarViewUIConfigurationModel extends CalendarViewUIConfiguration {
  constructor(
    dataEntity: DataEntityPresenterModel,
    config: DataViewsPresenterModel,
    private calendarConfig: DataViewCalendarFaceConfiguration,
  ) {
    super(dataEntity, config);
    this.display = true;
    this.displyedFields = this.initDisplyedFields();
  }

  initDisplyedFields(): FieldPresenterModel[] {
    const _viewOptions: {
      [key in keyof typeof FieldViewOptionValueTypeEnum]: string;
    } = {
      showAsTitle: this.calendarConfig.fieldsMapping.mainTitle,
      showAsSubTitle: this.calendarConfig.fieldsMapping.subTitle,
      showAsEntryPicture: this.calendarConfig.fieldsMapping.picture,
      showAsMainDate: this.calendarConfig.fieldsMapping.dateFields[0],
      showAsCategory: null,
      showAsSubCategory: null,
      showAsRelationField: null,
      showAsMainPhone: null,
      showAsMainNumber: null,
    };
    return (this.displyedFields = this.mapDisplayOptions(
      cloneDeep(this.dataEntity.fields),
      _viewOptions,
    ));
  }
}

export class CalendarViewUIConfigurationDefaultModel extends CalendarViewUIConfiguration {
  constructor(dataEntity: DataEntityPresenterModel) {
    super(dataEntity);
    this.display = true;
    this.displyedFields = this.initDisplyedFields();
  }

  initDisplyedFields(): FieldPresenterModel[] {
    return this.dataEntity.fields;
  }
}

export class TimelineViewUIConfigurationModel extends TimelineViewUIConfiguration {
  constructor(
    dataEntity: DataEntityPresenterModel,
    protected config: DataViewTimelineFaceConfiguration,
  ) {
    super(dataEntity);

    this.display = true;
    this.enablePublishing = this.config?.allowPublishPost;
    this.enableToShowStepName = false;
  }
}

export class TimelineViewUIConfigurationDefaultModel extends TimelineViewUIConfiguration {
  constructor(dataEntity: DataEntityPresenterModel) {
    super(dataEntity);

    this.display = true;
    this.enablePublishing = true;
    this.enableToShowStepName = true;
  }

  initDisplyedFields(): FieldPresenterModel[] {
    return this.dataEntity.fields;
  }
}
export class MiniCardViewUIConfigurationModel extends MiniCardViewUIConfiguration {
  constructor(
    dataEntity: DataEntityPresenterModel,
    config: DataViewsPresenterModel,
    private miniCardConfig: DataViewMiniCardFaceConfiguration,
  ) {
    super(dataEntity, config);
    this.display = true;
    this.displyedFields = this.initDisplyedFields();
  }
  initDisplyedFields(): FieldPresenterModel[] {
    const _viewOptions: {
      [key in keyof typeof FieldViewOptionValueTypeEnum]: string;
    } = {
      showAsTitle: this.miniCardConfig.fieldsMapping.mainTitle,
      showAsSubTitle: this.miniCardConfig.fieldsMapping.subTitle,
      showAsEntryPicture: this.miniCardConfig.fieldsMapping.picture,
      showAsMainDate: this.miniCardConfig.fieldsMapping.dateField,
      showAsCategory: null,
      showAsSubCategory: null,
      showAsRelationField: null,
      showAsMainPhone: null,
      showAsMainNumber: null,
    };
    return (this.displyedFields = this.mapDisplayOptions(
      cloneDeep(this.dataEntity.fields),
      _viewOptions,
    ));
  }
}
export class MiniCardViewUIConfigurationDefaultModel extends MiniCardViewUIConfiguration {
  constructor(dataEntity: DataEntityPresenterModel) {
    super(dataEntity);
    this.display = true;
    this.displyedFields = this.initDisplyedFields();
  }

  initDisplyedFields(): FieldPresenterModel[] {
    return this.dataEntity.fields;
  }
}
export class EditEntryConfigurationModel extends EditEntryConfiguration {
  constructor(
    newMode: boolean,
    dataView: DataViewsPresenterModel,
    dataEntity: DataEntityPresenterModel,
    disabledFieldsNames?: string[],
  ) {
    super(newMode, dataEntity, dataView, disabledFieldsNames);
  }

  override initDisplayedFields(newMode: boolean): FieldPresenterModel[] {
    const operation = newMode
      ? EntryActionOperationEnum.AllowCreateNewEntries
      : EntryActionOperationEnum.AllowEditEntries;
    const fields = this._getSupportedOperationConfiguration(operation)?.fields;

    if (fields) {
      return this.filterFields(fields);
    } else {
      return [];
    }
  }

  getMessage(operation: EntryActionOperationEnum): LocalizedValueModel {
    if (this.hasShowMessageAction(operation)) {
      return this._getSupportedOperationConfiguration(operation)?.afterSave
        ?.message;
    }
    return {
      en: 'The operation has been done successfully',
      ar: 'نجاح العملية',
    };
  }

  hasShowMessageAction(operation: EntryActionOperationEnum): boolean {
    return (
      this._getSupportedOperationConfiguration(operation)?.afterSave?.action ===
      AfterSaveActionEnum.ShowMessage
    );
  }

  override hasResetFormAction(operation: EntryActionOperationEnum): boolean {
    return (
      this._getSupportedOperationConfiguration(operation)?.afterSave?.action ===
      AfterSaveActionEnum.ResetForm
    );
  }

  override hasReturnToEntriesList(
    operation: EntryActionOperationEnum,
  ): boolean {
    return (
      this._getSupportedOperationConfiguration(operation)?.afterSave?.action ===
        AfterSaveActionEnum.RedirectToEntriesView &&
      this.dataView.viewConfiguration.enabled
    );
  }
  override hasReturnToEntryView(operation: EntryActionOperationEnum): boolean {
    return (
      this._getSupportedOperationConfiguration(operation)?.afterSave?.action ===
        AfterSaveActionEnum.RedirectToEntryView &&
      this.dataView.entriesConfiguration.enabled
    );
  }
}

export class EditEntryConfigurationDefaultModel extends EditEntryConfiguration {
  constructor(
    newMode: boolean,
    dataEntity: DataEntityPresenterModel,
    disableFieldsNames?: string[],
  ) {
    super(newMode, dataEntity, null, disableFieldsNames);
  }

  getMessage(): LocalizedValueModel {
    return { en: 'success', ar: 'نجاح العملية' };
  }
}
export class ViewEntryConfigurationDefaultModel extends ViewEntryConfiguration {
  constructor(dataEntity: DataEntityPresenterModel) {
    super(dataEntity);
  }
  getMessage(): LocalizedValueModel {
    return { en: 'success', ar: 'نجاح العملية' };
  }
  initDisplyedFields(): FieldPresenterModel[] {
    return this.dataEntity?.fields;
  }
}
export class ViewEntryConfigurationModel extends ViewEntryConfiguration {
  constructor(
    dataEntity: DataEntityPresenterModel,
    dataView: DataViewsPresenterModel,
  ) {
    super(dataEntity, dataView);

    this.displyedFields = this.initDisplyedFields();
    this.supportPaginationBetweenEntries =
      dataView.entriesConfiguration.supportPaginationBetweenEntries;
  }

  initDisplyedFields(): FieldPresenterModel[] {
    if (this.dataView?.entriesConfiguration?.fields) {
      return this.filterFields(this.dataView.entriesConfiguration.fields);
    } else {
      return [];
    }
  }

  getMessage(operation: EntryActionOperationEnum): LocalizedValueModel {
    if (this.hasShowMessageAction(operation)) {
      return this._getSupportedOperationConfiguration(operation)?.afterSave
        ?.message;
    }
    return null;
  }

  hasShowMessageAction(operation: EntryActionOperationEnum): boolean {
    return (
      this._getSupportedOperationConfiguration(operation)?.afterSave?.action ===
      AfterSaveActionEnum.ShowMessage
    );
  }

  override hasReturnToEntryView(operation: EntryActionOperationEnum): boolean {
    return (
      this._getSupportedOperationConfiguration(operation)?.afterSave?.action ===
        AfterSaveActionEnum.RedirectToEntryView &&
      this.dataView.entriesConfiguration.enabled
    );
  }
  override hasReturnToEntriesList(
    operation: EntryActionOperationEnum,
  ): boolean {
    return (
      this._getSupportedOperationConfiguration(operation)?.afterSave?.action ===
        AfterSaveActionEnum.RedirectToEntriesView &&
      this.dataView.viewConfiguration.enabled
    );
  }
}
